import {useState} from 'react';
import {useDebounce} from 'use-debounce';
import {areAnyPropsSet} from '../utils/object/areAnyPropsSet';
import {useWhenChangedEffect} from '@netx/core-components/hooks/useWhenChangedEffect';

export type UseFilterSortQueryParams = {
	filters?: Record<string, any>;
	sort?: Record<string, any>;
	dataRef?: React.MutableRefObject<any[]>;
	setQueryParams: (
		value: React.SetStateAction<{filters?: any; sort?: any}>,
	) => void;
};

/**
 * Hook to handle filter and sort queries\
 * This hook will handle the debouncing of the filter and sort queries and will set the dataReady and filtered states accordingly
 *
 * @example
 * ```tsx
 * const dataRef = useRef([]);
 * const [queryParams, setQueryParams] = useState({ filters: {}, sort: {} });
 * const { dataReady, filtered, setDataReady } = useFilterSortQuery({
 * 	filters,
 * 	sort,
 * 	dataRef,
 * 	setQueryParams,
 * });
 * ```
 */
export const useFilterSortQuery = ({
	filters,
	sort,
	dataRef,
	setQueryParams,
}: UseFilterSortQueryParams) => {
	const [dataReady, setDataReady] = useState(false);
	const [filtered, setFiltered] = useState(false);

	// delay repeated requests
	const [debouncedSort] = useDebounce(sort, 200);
	const [debouncedFilters] = useDebounce(filters, 200);

	useWhenChangedEffect(
		[debouncedFilters, debouncedSort] as const,
		([filters, sort], old) => {
			if (dataRef) dataRef.current = [];
			setFiltered(areAnyPropsSet(filters));
			if (old?.[0] !== filters) setDataReady(false);
			setQueryParams({
				filters,
				sort,
			});
		},
		{
			executeAtMount: true,
		},
	);

	return {
		dataReady,
		filtered,
		setDataReady,
	};
};

export default useFilterSortQuery;
